import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';

type Theme = 'light' | 'dark';

interface ThemeState {
  theme: Theme;
  toggle: () => void;
}

const ThemeContext = createContext<ThemeState | null>(null);
const STORAGE_KEY = 'gh_theme';

/** --gh-surface per theme. The header is that colour, and on a phone the status
 *  bar sits directly above the header — a mismatch reads as a rendering bug. */
const STATUS_BAR: Record<Theme, string> = { light: '#ffffff', dark: '#182130' };

function initialTheme(): Theme {
  const attr = document.documentElement.dataset.theme;
  return attr === 'dark' ? 'dark' : 'light';
}

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [theme, setTheme] = useState<Theme>(initialTheme);

  useEffect(() => {
    document.documentElement.dataset.theme = theme;
    // The inline script in index.html sets this once, from the stored theme.
    // Without repeating it here, toggling to dark leaves a white status bar.
    document.querySelector('meta[name="theme-color"]')?.setAttribute('content', STATUS_BAR[theme]);
    try {
      localStorage.setItem(STORAGE_KEY, theme);
    } catch {
      /* private mode — the inline script in index.html falls back to light */
    }
  }, [theme]);

  const toggle = useCallback(() => setTheme((t) => (t === 'dark' ? 'light' : 'dark')), []);
  const value = useMemo(() => ({ theme, toggle }), [theme, toggle]);

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme(): ThemeState {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error('useTheme yalnızca ThemeProvider içinde kullanılabilir.');
  return ctx;
}
